import { readCookie } from "modules/cookieManager";
import CompletionSuggestionType from "enums/completionSuggestionType";
import StringExtensions from "utils/stringExtensions";

/**
 * A utility class for search bar related helper functions.
 */
export default class SearchBarHelper {
  /**
   * Trims leading and trailing whitespace from the given text.
   * If the text is null or undefined, returns an empty string.
   * @param {string} text - The text to trim.
   * @returns {string} The trimmed text, or an empty string if the input is null or undefined.
   */
  static trimText(text) {
    return text?.trim() ?? '';
  }

  /**
   * Checks if the provided text is null, undefined, or consists only of whitespace characters.
   * @param {string} text - The text to check.
   * @returns {boolean} True if the text is null, undefined, or whitespace; otherwise, false.
   */
  static isNullOrWhiteSpace(text) {
    return (text?.trim().length ?? 0) === 0;
  }

  /**
   * Retrieves the last search text from the 'Personal' cookie.
   * @returns {string} The last search text, or an empty string if not found.
   */
  static getLastSearchText() {
    const personalCookie = readCookie('Personal', true);
    const parameters = new URLSearchParams(personalCookie);
    return parameters.get('searchtext') || '';
  }

  /**
   * Determines whether to hide the "Use my current location" option based on the search text and a force hide flag.
   * @param {string} searchText - The current search text input by the user.
   * @param {boolean} forceHide - Whether to forcefully hide the location option.
   * @returns {boolean} True if the location option should be hidden, false otherwise.
   */
  static shouldHideCurrentLocation(searchText, forceHide = false) {
    return forceHide || !SearchBarHelper.isNullOrWhiteSpace(searchText);
  }

  /**
   * Extracts the results array from the search results object.
   * @param {Object} searchResults - The search results object.
   * @returns {Array} The extracted results array, or an empty array if not present.
   */
  static extractResults(searchResults) {
    return searchResults?.Results ?? [];
  }

  /**
   * Formats the search text based on the location type.
   * @param {Object} location - The location object.
   * @returns {string} The formatted search text.
   */
  static getFormattedSearchText(location) {
    if (!location) {
      return '';
    }

    const locationType = location.completionSuggestionType;
    let formattedSearchText = '';

    switch (locationType) {
      case CompletionSuggestionType.Neighborhood.name: {
        formattedSearchText = `
          ${location.name} ${StringExtensions.toCapitalizedCase(locationType)},
          ${StringExtensions.toCapitalizedCase(location.marketName)},
          ${StringExtensions.toCapitalizedCase(location.marketState)}
        `;
        formattedSearchText = StringExtensions.normalizeSpaces(formattedSearchText);
        break;
      }
      case CompletionSuggestionType.County.name: {
        formattedSearchText = `${location.name} County, ${location.marketState}`;
        break;
      }
      case CompletionSuggestionType.Zip.name: {
        formattedSearchText = `${location.postalCode}`;
        break;
      }
      case CompletionSuggestionType.City.name: {
        formattedSearchText = `${location.city}, ${location.marketState}`;
        break;
      }
      case CompletionSuggestionType.SchoolDistrict.name: {
        formattedSearchText = `${location.name}`;
        break;
      }
      case CompletionSuggestionType.Market.name: {
        const stateAbbreviation = location.marketState;
        formattedSearchText = `${location.marketName}, ${stateAbbreviation} Area`;
        break;
      }
      default: {
        formattedSearchText = '';
        break;
      }
    }

    const normalizedText = StringExtensions.normalizeSpaces(formattedSearchText);
    return normalizedText;
  }
}